
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Bot, Workflow, Zap, Shield, Users, Code } from 'lucide-react';
import { Link } from 'react-router-dom';

const AIAgentDevelopment = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              AI Agent Development
            </h1>
            <p className="text-lg text-gray-700">
              Our custom AI agents are designed to handle specific tasks within various roles, 
              freeing your human talent to focus on higher-value work that requires creativity, 
              emotional intelligence, and complex decision-making.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <Card className="border border-[#0EA5E9]/20 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Bot className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <h3 className="text-xl font-bold mb-3">Custom AI Agents</h3>
                <p className="text-gray-700 mb-4">
                  We develop AI agents tailored to your specific business processes, workflows, and needs.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Personalized to your workflows</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Built for specific roles and tasks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Continuous learning and improvement</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card className="border border-[#0EA5E9]/20 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Workflow className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <h3 className="text-xl font-bold mb-3">Workflow Integration</h3>
                <p className="text-gray-700 mb-4">
                  Seamlessly integrate AI agents into your existing tools and workflows.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>API integrations with your tech stack</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>No-code interfaces for non-technical users</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Comprehensive documentation and support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card className="border border-[#0EA5E9]/20 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <h3 className="text-xl font-bold mb-3">Performance Optimization</h3>
                <p className="text-gray-700 mb-4">
                  Continuously optimize your AI agents for maximum efficiency and effectiveness.
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Regular performance reviews</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Data-driven improvements</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#0EA5E9] mr-2">•</span>
                    <span>Adaptation to changing business needs</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          <div className="max-w-3xl mx-auto mt-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-[#0EA5E9]">Development Process</h2>
            
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0EA5E9]">1</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold mb-2">Requirements Analysis</h3>
                  <p className="text-gray-700">
                    We work closely with your team to understand your specific needs, processes, and pain points.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0EA5E9]">2</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold mb-2">Agent Design & Development</h3>
                  <p className="text-gray-700">
                    Our team designs and develops custom AI agents tailored to your specific requirements.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0EA5E9]">3</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold mb-2">Integration & Testing</h3>
                  <p className="text-gray-700">
                    We integrate the AI agents into your existing workflows and thoroughly test them.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0EA5E9]">4</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold mb-2">Deployment & Training</h3>
                  <p className="text-gray-700">
                    We deploy the AI agents and provide training to your team on how to work with them.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#0EA5E9]">5</span>
                </div>
                <div className="flex-grow">
                  <h3 className="text-xl font-bold mb-2">Ongoing Support & Optimization</h3>
                  <p className="text-gray-700">
                    We provide ongoing support and continuously optimize the AI agents based on performance data.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <Link to="/join-waitlist">
              <Button className="bg-gradient-to-r from-[#0EA5E9] to-[#3B82F6] hover:from-[#0EA5E9]/90 hover:to-[#3B82F6]/90 px-8 py-6 text-lg">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AIAgentDevelopment;
